import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { TypewriterText } from '../hooks/useTypewriter';
import macName from '../lib/macName';

// Chat with your own models, from any device. The model runs on one of your macs, in the
// soma app; this page asks it through remote control (routes/remote.py) and types the
// reply out as the app streams it back.

const API = '/api';
const LIVE = 'rgb(77, 255, 133)';
const FINAL = ['done', 'failed', 'expired'];

function headers() {
  const h = { 'Content-Type': 'application/json' };
  const t = localStorage.getItem('logos_session_token');
  if (t) h['Authorization'] = `Bearer ${t}`;
  return h;
}

const reduced = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// a reply, typed out: it reveals characters at a pace that keeps up with the stream, faster
// when it falls behind, so text arriving in bursts still reads as typing
function TypedReply({ text, live }) {
  const [shown, setShown] = useState(live && !reduced() ? 0 : text.length);
  const pos = useRef(shown);
  useEffect(() => {
    if (reduced()) { pos.current = text.length; setShown(text.length); return undefined; }
    let frame;
    const step = () => {
      const behind = text.length - pos.current;
      if (behind <= 0) return;
      pos.current = Math.min(text.length, pos.current + Math.max(0.6, behind / 14));
      setShown(Math.floor(pos.current));
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [text]);
  const typing = live || shown < text.length;
  return (
    <span className="whitespace-pre-wrap">
      {text.slice(0, shown)}
      {typing && <span className="crt-cursor" aria-hidden="true">_</span>}
    </span>
  );
}

// follow a command until it settles: a long poll that answers as the app reports more
async function follow(command, onUpdate, stopped) {
  let { status } = command;
  let seen = command.result?.text?.length ?? 0;
  while (!stopped() && !FINAL.includes(status)) {
    try {
      const res = await fetch(`${API}/me/commands/${command.command_id}?wait=20&status=${status}&seen=${seen}`,
        { headers: headers(), credentials: 'include' });
      if (!res.ok) { await new Promise((r) => setTimeout(r, 1500)); continue; }
      const now = await res.json();
      status = now.status;
      seen = now.result?.text?.length ?? 0;
      if (!stopped()) onUpdate(now);
    } catch {
      await new Promise((r) => setTimeout(r, 1500));
    }
  }
}

function Button({ children, onClick, disabled, primary, testid }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`border px-4 py-1.5 rounded-full font-mono text-xs lowercase transition-all disabled:opacity-40 disabled:pointer-events-none ${
        primary ? 'border-white bg-white text-black hover:bg-transparent hover:text-white' : 'border-[#383f38] text-white hover:bg-white hover:text-black'}`}>
      {children}
    </button>
  );
}

function Picker({ value, options, onChange, testid, label }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} aria-label={label}
      className="bg-transparent border border-[#383f38] rounded-lg px-2 py-1.5 font-mono text-xs text-white outline-none focus:border-white min-w-0 max-w-full">
      {options.map((o) => <option key={o.id} value={o.id} className="bg-black">{o.label}</option>)}
    </select>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-black py-10 px-4 sm:px-6 lg:px-8 page-wrapper" data-testid="chat-page">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl lg:text-5xl font-bold text-white lowercase font-mono"><TypewriterText text="chat" speed={65} /></h1>
        {children}
      </div>
    </div>
  );
}

export default function Chat() {
  const { user, loading, login } = useAuth();
  const [params, setParams] = useSearchParams();
  const [apps, setApps] = useState(null);
  const [macId, setMacId] = useState(params.get('mac') || '');
  const [modelId, setModelId] = useState(params.get('model') || '');
  const [turns, setTurns] = useState([]);        // {key, you, reply, status, error, live}
  const [working, setWorking] = useState('');     // what the mac is doing for us: loading, switching…
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [learn, setLearn] = useState(false);
  const alive = useRef(true);
  const end = useRef(null);
  useEffect(() => () => { alive.current = false; }, []);

  const loadApps = useCallback(() => fetch(`${API}/me/apps`, { headers: headers(), credentials: 'include' })
    .then((r) => (r.ok ? r.json() : null)).then((d) => { if (d && alive.current) setApps(d.apps); }), []);
  useEffect(() => {
    if (!user) return undefined;
    loadApps();
    const timer = setInterval(loadApps, 3000);
    return () => clearInterval(timer);
  }, [user, loadApps]);

  const mac = apps?.find((a) => a.token_id === macId) || apps?.find((a) => a.online) || apps?.[0];
  const state = mac?.state || {};
  const chat = state.chat || {};
  const training = state.training || {};
  const models = (state.checkpoints || []).filter((c) => c.chat_ok !== false);
  const model = models.find((c) => c.id === modelId) || models.find((c) => c.name === chat.name) || models[0];
  const loaded = Boolean(chat.loaded);
  const ready = loaded && model && chat.name === model.name;
  const online = Boolean(mac?.online);

  // keep the address shareable across your own devices: /chat?mac=…&model=…
  useEffect(() => {
    if (!mac) return;
    const next = new URLSearchParams(params);
    next.set('mac', mac.token_id);
    if (model) next.set('model', model.id); else next.delete('model');
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [mac, model, params, setParams]);

  const updateTurn = useCallback((now) => setTurns((ts) => ts.map((t) => (t.key === now.command_id ? {
    ...t, status: now.status, reply: now.result?.text ?? t.reply, error: now.result?.error,
    live: !FINAL.includes(now.status),
  } : t))), []);

  // the conversation so far: the messages sent since this model was last loaded on that mac,
  // from any device, and any reply still being written
  const macKey = mac?.token_id;
  const loadedName = loaded ? chat.name : '';
  useEffect(() => {
    if (!macKey || !loadedName) { setTurns([]); return undefined; }
    let stopped = false;
    fetch(`${API}/me/apps/${macKey}/commands?limit=80`, { headers: headers(), credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null)).then((d) => {
        if (!d || stopped) return;
        const log = [...d.commands].reverse();
        const lastLoad = log.map((c) => c.kind === 'chat.load' && c.status === 'done').lastIndexOf(true);
        const since = log.slice(lastLoad + 1).filter((c) => c.kind === 'chat.prompt');
        setTurns(since.map((c) => ({ key: c.command_id, you: c.args.text, reply: c.result?.text || '', status: c.status,
          error: c.result?.error, live: false })));
        since.filter((c) => !FINAL.includes(c.status)).forEach((c) => {
          setTurns((ts) => ts.map((t) => (t.key === c.command_id ? { ...t, live: true } : t)));
          follow(c, updateTurn, () => stopped || !alive.current);
        });
      });
    return () => { stopped = true; };
  }, [macKey, loadedName, updateTurn]);

  useEffect(() => { end.current?.scrollIntoView({ block: 'nearest' }); }, [turns]);

  const send = async (kind, args = {}) => {
    setError(null);
    const res = await fetch(`${API}/me/apps/${mac.token_id}/commands`, {
      method: 'POST', headers: headers(), credentials: 'include', body: JSON.stringify({ kind, args }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { setError(body.detail || 'your mac could not be asked'); return null; }
    return body;
  };

  // a command we wait on, start to finish (load, unload, stop training)
  const run = async (kind, args, label) => {
    setWorking(label);
    const command = await send(kind, args);
    let last = command;
    if (command) await follow(command, (now) => { last = now; }, () => !alive.current);
    if (alive.current) {
      setWorking('');
      if (last && last.status !== 'done') setError(last.result?.error || `${label.replace('…', '')}: ${last.status}`);
      loadApps();
    }
    return last?.status === 'done';
  };

  const open = async () => {
    if (training.running && !(await run('train.stop', { save: true }, 'stopping training…'))) return;
    if (loaded && !(await run('chat.unload', { save: true }, `saving and closing ${chat.name}…`))) return;
    await run('chat.load', { checkpoint: model.id }, `loading ${model.name}…`);
  };

  const say = async (e) => {
    e?.preventDefault();
    const text = message.trim();
    if (!text || !ready) return;
    setMessage('');
    const command = await send('chat.prompt', { text, learn });
    if (!command) return;
    setTurns((ts) => [...ts, { key: command.command_id, you: text, reply: '', status: command.status, live: true }]);
    follow(command, updateTurn, () => !alive.current);
  };

  const replying = turns.some((t) => t.live);

  if (loading) return <div className="min-h-screen bg-black" />;
  if (!user) {
    return (
      <Shell>
        <p className="text-[#aaaaaa] mt-3">talk to your models from any device. they run on your mac, in the soma app; sign in here and on the app, and that's it.</p>
        <div className="mt-6"><Button primary onClick={login} testid="chat-login">sign in</Button></div>
      </Shell>
    );
  }
  if (apps && apps.length === 0) {
    return (
      <Shell>
        <p className="text-[#aaaaaa] mt-3">your models run on your mac, so chat needs one linked. open the soma app, sign in to logOS in its settings, and come back.</p>
        <div className="mt-6 flex gap-3"><Link to="/downloads"><Button>get soma</Button></Link></div>
      </Shell>
    );
  }

  return (
    <Shell>
      <p className="text-[#aaaaaa] mt-3">talk to a model on one of your macs, from any device. the mac does the thinking: it needs the soma app open.</p>

      {!apps ? <p className="font-mono text-sm text-[#666666] lowercase mt-8">loading…</p> : (
        <>
          <div className="mt-8 border-y border-[#1a201b] py-3 flex flex-wrap items-center gap-2 font-mono text-xs" data-testid="chat-controls">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: online ? LIVE : '#444444' }} />
            <Picker label="mac" value={mac.token_id} onChange={(id) => { setMacId(id); setModelId(''); }} testid="chat-mac"
              options={apps.map((a) => ({ id: a.token_id, label: `${macName(a.name)}${a.online ? '' : ' · offline'}` }))} />
            {models.length > 0 && (
              <Picker label="model" value={model.id} onChange={setModelId} testid="chat-model"
                options={models.map((c) => ({ id: c.id, label: c.name }))} />
            )}
            <span className="ml-auto flex gap-2 items-center">
              {ready ? (
                <>
                  {chat.dirty && <Button disabled={!online || replying || Boolean(working)} onClick={() => run('chat.save', {}, 'saving…')}>save</Button>}
                  <Button disabled={!online || replying || Boolean(working)} onClick={() => run('chat.unload', { save: true }, 'closing…')}>close</Button>
                </>
              ) : models.length > 0 && (
                <Button primary disabled={!online || Boolean(working)} onClick={open} testid="chat-open">
                  {training.running ? 'stop training and chat' : loaded ? `switch to ${model.name}` : 'start chatting'}
                </Button>
              )}
            </span>
          </div>

          <p className="font-mono text-[11px] text-[#666666] lowercase mt-2 min-h-[1rem]" data-testid="chat-status">
            {working || (!online ? `${macName(mac.name)} is offline. open the soma app on it.`
              : models.length === 0 ? 'no models on this mac that can chat yet.'
              : ready ? `${chat.name} is listening on ${macName(mac.name)}`
              : training.running ? `${macName(mac.name)} is training ${training.name || ''}. chatting stops that, and saves it first.`
              : loaded ? `${chat.name} is open on ${macName(mac.name)}` : '')}
          </p>
          {error && <p className="font-mono text-xs text-[#ff8888] lowercase mt-1">{error}</p>}

          {ready && (
            <div className="mt-6" data-testid="chat-conversation">
              <div className="space-y-5">
                {turns.length === 0 && <p className="font-mono text-xs text-[#555555] lowercase">say something to {chat.name}.</p>}
                {turns.map((t) => (
                  <div key={t.key} className="space-y-1.5">
                    <p className="text-[#888888] text-sm whitespace-pre-wrap"><span className="font-mono text-[10px] text-[#555555] mr-2">you</span>{t.you}</p>
                    <p className="text-white font-mono text-sm leading-relaxed">
                      <span className="text-[10px] text-[#555555] mr-2">{chat.name}</span>
                      {t.reply ? <TypedReply text={t.reply} live={t.live} />
                        : t.status === 'failed' || t.status === 'expired'
                          ? <span className="text-[#ff8888] text-xs">{t.error || (t.status === 'expired' ? 'your mac didn\'t pick this up' : 'failed')}</span>
                          : <span className="crt-cursor text-[#888888]" aria-label="thinking">_</span>}
                    </p>
                  </div>
                ))}
                <div ref={end} />
              </div>

              <form onSubmit={say} className="sticky bottom-0 bg-black/90 backdrop-blur pt-4 pb-3 mt-6 space-y-2">
                <div className="flex gap-2">
                  <input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="message" disabled={!online}
                    enterKeyHint="send" autoComplete="off"
                    className="flex-1 min-w-0 bg-transparent border border-[#383f38] rounded-lg px-3 py-2 text-white text-base sm:text-sm outline-none focus:border-white placeholder:text-[#555555]"
                    data-testid="chat-message" />
                  {replying
                    ? <Button onClick={(e) => { e.preventDefault(); send('chat.cancel'); }}>stop</Button>
                    : <Button primary disabled={!online || !message.trim()} testid="chat-send">send</Button>}
                </div>
                <label className="flex items-center gap-1.5 font-mono text-[11px] text-[#888888] lowercase">
                  <input type="checkbox" checked={learn} onChange={(e) => setLearn(e.target.checked)} />
                  learn from this conversation
                </label>
              </form>
            </div>
          )}
        </>
      )}
    </Shell>
  );
}
